import { useCallback, useEffect, useState } from 'react'
import { Button, Card, message, Popconfirm, Space, Spin, Tag, Typography } from 'antd'
import {
  ApiOutlined,
  ClockCircleOutlined,
  CloudServerOutlined,
  RobotOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import * as svcApi from '@/api/services'
import type { ServicesStatus } from '@/api/services'
import { startCapture, stopCapture } from '@/api/capture'
import type { CaptureStatus } from '@/types'
import '../../styles/aurora-page.css'

const { Title, Text } = Typography

/** 服务定义（展示顺序即渲染顺序） */
const SERVICES: { key: 'mock' | 'worker' | 'scheduler'; name: string; desc: string }[] = [
  { key: 'mock', name: 'Mock 服务', desc: '接口 Mock 规则匹配与响应服务（随主应用进程运行，端口 8001）' },
  { key: 'worker', name: 'Playwright 引擎', desc: 'UI 自动化执行引擎（独立 Node 进程，消费 Redis 执行队列）' },
  { key: 'scheduler', name: '定时调度器', desc: '通用定时器 cron 触发调度（随主应用进程运行，暂停后所有定时计划暂不触发）' },
]

export default function Services() {
  const [status, setStatus] = useState<ServicesStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [acting, setActing] = useState<string | null>(null)
  // 抓包代理（统一在此管理启停）
  const [captureStatus, setCaptureStatus] = useState<CaptureStatus | null>(null)
  const [captureActing, setCaptureActing] = useState(false)

  const loadStatus = useCallback(async () => {
    setLoading(true)
    try {
      const s = await svcApi.getServicesStatus()
      setStatus(s)
      // 抓包代理状态已并入 /services/status 统一返回
      setCaptureStatus(s.capture)
    } finally {
      setLoading(false)
    }
  }, [])

  // 进入页面一次性查询，不轮询（可通过「刷新」按钮手动更新）
  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  const handleStartCapture = async () => {
    setCaptureActing(true)
    try {
      await startCapture()
      message.success(`抓包代理已启动，请将客户端 HTTP 代理指向本机 ${captureStatus?.port ?? 8080}`)
      await loadStatus()
    } catch {
      /* 拦截器已提示 */
    } finally {
      setCaptureActing(false)
    }
  }

  const handleStopCapture = async () => {
    setCaptureActing(true)
    try {
      await stopCapture()
      message.success('抓包代理已停止')
      await loadStatus()
    } catch {
      /* 拦截器已提示 */
    } finally {
      setCaptureActing(false)
    }
  }

  const handleStart = async (key: 'mock' | 'worker' | 'scheduler') => {
    setActing(key)
    try {
      await svcApi.startService(key)
      message.success('启动指令已下发')
      await loadStatus()
    } catch {
      message.error('启动失败')
    } finally {
      setActing(null)
    }
  }

  const handleStop = async (key: 'mock' | 'worker' | 'scheduler') => {
    setActing(key)
    try {
      await svcApi.stopService(key)
      message.success('已停止')
      await loadStatus()
    } catch {
      message.error('停止失败')
    } finally {
      setActing(null)
    }
  }

  const handlePause = async () => {
    setActing('scheduler')
    try {
      await svcApi.pauseScheduler()
      message.success('调度器已暂停，定时计划暂不触发')
      await loadStatus()
    } catch {
      message.error('暂停失败')
    } finally {
      setActing(null)
    }
  }

  const handleResume = async () => {
    setActing('scheduler')
    try {
      await svcApi.resumeScheduler()
      message.success('调度器已恢复，定时计划继续触发')
      await loadStatus()
    } catch {
      message.error('恢复失败')
    } finally {
      setActing(null)
    }
  }

  return (
    <div className="aurora-page">
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>服务管理</Title>
        <Space>
          <Button onClick={loadStatus}>刷新</Button>
        </Space>
      </div>

      <div className="content-card" style={{ padding: 20 }}>
        <Spin spinning={loading && !status}>
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            {SERVICES.map((svc) => {
              const info = status?.[svc.key]
              const workerInfo = svc.key === 'worker' ? (info as svcApi.WorkerServiceStatus | undefined) : undefined
              const mockInfo = svc.key === 'mock' ? (info as svcApi.MockServiceStatus | undefined) : undefined
              const schedulerInfo = svc.key === 'scheduler' ? (info as svcApi.SchedulerServiceStatus | undefined) : undefined
              const running = info?.running
              return (
                <Card
                  key={svc.key}
                  size="small"
                  style={{ borderRadius: 10, border: '1px solid var(--au-border)' }}
                  styles={{ body: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' } }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--au-primary-bg, rgba(0,0,0,0.04))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: 'var(--au-primary)' }}>
                    {svc.key === 'mock' ? <CloudServerOutlined /> : svc.key === 'scheduler' ? <ClockCircleOutlined /> : <RobotOutlined />}
                  </div>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <Space size={10}>
                      <Text strong style={{ fontSize: 14 }}>{svc.name}</Text>
                      <Tag
                        color={info === undefined ? 'default' : running ? 'success' : schedulerInfo?.paused ? 'warning' : 'error'}
                        style={{ margin: 0 }}
                      >
                        {info === undefined ? '检测中' : running ? '运行中' : schedulerInfo?.paused ? '已暂停' : '未启动'}
                      </Tag>
                    </Space>
                    <div>
                      <Text style={{ fontSize: 12, color: 'var(--au-text-faint)' }}>{svc.desc}</Text>
                    </div>
                    {info && (
                      <div style={{ marginTop: 4 }}>
                        {svc.key === 'mock' && mockInfo && (
                          <Text code style={{ fontSize: 12 }}>{mockInfo.address}/mock/{'{project_id}'}/你的接口路径</Text>
                        )}
                        {svc.key === 'worker' && workerInfo && (
                          <Text code style={{ fontSize: 12 }}>
                            {workerInfo.pid ? `PID ${workerInfo.pid} | ` : ''}
                            {workerInfo.last_beat ? `最近活动 ${new Date(workerInfo.last_beat).toLocaleTimeString('zh-CN', { hour12: false })} | ` : ''}
                            队列待执行 {workerInfo.queue_len} 个
                          </Text>
                        )}
                        {svc.key === 'scheduler' && schedulerInfo && (
                          <Text code style={{ fontSize: 12 }}>
                            已注册定时任务 {schedulerInfo.job_count} 个
                            {schedulerInfo.paused ? '（暂停期间到点的计划不补跑）' : ''}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                  <Space>
                    {svc.key === 'scheduler' ? (
                      schedulerInfo?.paused ? (
                        <Button type="primary" loading={acting === 'scheduler'} onClick={handleResume}>
                          恢复
                        </Button>
                      ) : running ? (
                        <Popconfirm
                          title="确认暂停定时调度器？暂停期间所有定时计划到点不执行且不补跑。"
                          onConfirm={handlePause}
                          okText="暂停"
                          cancelText="取消"
                          okButtonProps={{ danger: true }}
                        >
                          <Button danger loading={acting === 'scheduler'}>暂停</Button>
                        </Popconfirm>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 12 }}>随主应用启动</Text>
                      )
                    ) : running ? (
                      <Popconfirm
                        title={`确认停止${svc.name}？`}
                        onConfirm={() => handleStop(svc.key)}
                        okText="停止"
                        cancelText="取消"
                        okButtonProps={{ danger: true }}
                      >
                        <Button danger loading={acting === svc.key}>停止</Button>
                      </Popconfirm>
                    ) : (
                      <Button type="primary" loading={acting === svc.key} onClick={() => handleStart(svc.key)}>
                        启动
                      </Button>
                    )}
                  </Space>
                </Card>
              )
            })}

            {/* 抓包代理（统一管理） */}
            <Card
              size="small"
              style={{ borderRadius: 10, border: '1px solid var(--au-border)' }}
              styles={{ body: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' } }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--au-primary-bg, rgba(0,0,0,0.04))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: 'var(--au-primary)' }}>
                <ThunderboltOutlined />
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <Space size={10}>
                  <Text strong style={{ fontSize: 14 }}>抓包代理</Text>
                  <Tag color={captureStatus === null ? 'default' : captureStatus.running ? 'success' : 'error'} style={{ margin: 0 }}>
                    {captureStatus === null ? '检测中' : captureStatus.running ? '运行中' : '未启动'}
                  </Tag>
                </Space>
                <div>
                  <Text style={{ fontSize: 12, color: 'var(--au-text-faint)' }}>
                    HTTP(S) 代理式抓包服务（随主应用进程运行，默认端口 8080；HTTPS 抓包需在客户端安装并信任平台 CA 证书）
                  </Text>
                </div>
                {captureStatus && (
                  <div style={{ marginTop: 4 }}>
                    <Text code style={{ fontSize: 12 }}>
                      监听 0.0.0.0:{captureStatus.port}
                      {captureStatus.started_at ? ` | 启动于 ${captureStatus.started_at}` : ''}
                      {` | 待入库 ${captureStatus.pending_count} | 背压丢弃 ${captureStatus.dropped_count}`}
                    </Text>
                  </div>
                )}
              </div>
              <Space>
                {captureStatus?.running ? (
                  <Popconfirm
                    title="确认停止抓包代理？停止后客户端请求将无法通过代理转发。"
                    onConfirm={handleStopCapture}
                    okText="停止"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                  >
                    <Button danger loading={captureActing}>停止</Button>
                  </Popconfirm>
                ) : (
                  <Button type="primary" loading={captureActing} onClick={handleStartCapture}>
                    启动
                  </Button>
                )}
              </Space>
            </Card>
          </Space>
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ApiOutlined style={{ color: 'var(--au-text-faint)' }} />
            <Text style={{ fontSize: 12, color: 'var(--au-text-faint)' }}>
              状态实时探测（Mock 查进程内运行标记，引擎查进程与 Redis 注册信息，调度器查 APScheduler 运行态），进入页面查询一次，不做自动轮询
            </Text>
          </div>
        </Spin>
      </div>
    </div>
  )
}
